import React from "react";
import { Link } from "react-router-dom";
import { Bot, Sparkles } from "lucide-react";

// QHUB.BUDDY Icon Only Component
export const QHubIcon = ({ size = "default" }) => {
  const sizes = {
    small: "w-8 h-8",
    default: "w-10 h-10",
    large: "w-14 h-14",
    xl: "w-20 h-20",
  };
  const s = sizes[size] || sizes.default;
  return (
    <div className={`${s} relative`}>
      <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-[#419cca] to-[#8b5cf6] animate-gradient opacity-20 blur-lg" />
      <div className="relative w-full h-full rounded-xl bg-gradient-to-br from-[#419cca] to-[#8b5cf6] flex items-center justify-center shadow-lg shadow-[#419cca]/20">
        <Bot className="w-1/2 h-1/2 text-white" />
        <Sparkles className="absolute -top-1 -right-1 w-3 h-3 text-yellow-400" />
      </div>
    </div>
  );
};

// QHUB.BUDDY Logo Component (uses uploaded brand asset)
export const QHubBuddyLogo = ({ size = "default", showText = true }) => {
  // Aspect ratio ≈ 5:1 (transparent-cropped asset)
  const heights = {
    small: "h-9",
    default: "h-12",
    large: "h-16",
  };
  const h = heights[size] || heights.default;
  void showText;
  return (
    <img
      src="/qhub-buddy-logo.png"
      alt="QHUB.BUDDY"
      className={`${h} w-auto select-none`}
      draggable={false}
    />
  );
};

// QHUB Header Component - Light Theme
export const QHubHeader = ({ user, onLogout, showNav = true }) => {
  return (
    <header className="qhub-header sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-20">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center">
            <QHubBuddyLogo size="default" />
          </Link>

          {/* Navigation - Desktop */}
          {showNav && (
            <nav className="hidden md:flex items-center gap-8 ml-10">
              <a
                href="https://www.qhubglobal.com/#page-top"
                target="_blank"
                rel="noopener noreferrer"
                className="qhub-nav-link text-sm font-medium"
              >
                Inicio QHUB
              </a>
              <a
                href="https://www.qhubglobal.com/#section-661819b668097"
                target="_blank"
                rel="noopener noreferrer"
                className="qhub-nav-link text-sm font-medium"
              >
                Acerca de
              </a>
              <a
                href="https://www.qhubglobal.com/#section-661819b680179"
                target="_blank"
                rel="noopener noreferrer"
                className="qhub-nav-link text-sm font-medium"
              >
                Servicios
              </a>
              <a
                href="https://www.qhubglobal.com/#section-661819b983c62"
                target="_blank"
                rel="noopener noreferrer"
                className="qhub-nav-link text-sm font-medium"
              >
                Contacto
              </a>
            </nav>
          )}

          {/* Right side */}
          <div className="flex items-center gap-4 ml-auto">
            {user && (
              <span className="text-sm text-gray-600 hidden sm:block">
                {user.name}
              </span>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

// QHUB Footer Component - Light Theme
export const QHubFooter = () => {
  return (
    <footer className="qhub-footer py-8 mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <QHubBuddyLogo size="small" />
            </div>
            <p className="text-gray-500 text-sm max-w-md">
              Plataforma educativa con inteligencia artificial y las mejores 
              metodologías globales, adaptada a la realidad de cada país.
            </p>
          </div>

          {/* Links */}
          <div>
            <h4 className="text-gray-900 font-semibold mb-4">QHUB</h4>
            <ul className="space-y-2">
              <li>
                <a
                  href="https://www.qhubglobal.com/#section-661819b668097"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-[#419cca] text-sm transition-colors"
                >
                  Acerca de nosotros
                </a>
              </li>
              <li>
                <a
                  href="https://www.qhubglobal.com/#section-661819b680179"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-[#419cca] text-sm transition-colors"
                >
                  Servicios
                </a>
              </li>
              <li>
                <a
                  href="https://www.qhubglobal.com/#section-661819b89d02e"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-[#419cca] text-sm transition-colors"
                >
                  Socios Estratégicos
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-gray-900 font-semibold mb-4">Contacto</h4>
            <ul className="space-y-2 text-sm text-gray-500">
              <li>
                <a href="mailto:contact@qhubglobal.com" className="hover:text-[#419cca] transition-colors">
                  contact@qhubglobal.com
                </a>
              </li>
              <li>Educación Global</li>
              <li>Disponible en todo el mundo</li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-gray-200 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm">
            © {new Date().getFullYear()} QHUB. Todos los derechos reservados.
          </p>
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <span>Powered by</span>
            <span className="text-[#419cca] font-semibold">QHUB AI Solutions</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default { QHubBuddyLogo, QHubIcon, QHubHeader, QHubFooter };
